import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';

const OTPEditor = ({
  length = 6,
  value,
  onChange,
  onComplete,
  autoFocus = true,
  disabled = false,
  error,
  helperText,
}) => {
  const [internal, setInternal] = useState(Array.from({ length }, () => ''));
  const inputsRef = useRef([]);

  const digits = useMemo(() => {
    if (typeof value === 'string') {
      const cleaned = value.replace(/\D/g, '').slice(0, length);
      const arr = Array.from({ length }, (_, i) => cleaned[i] ?? '');
      return arr;
    }
    return internal;
  }, [internal, length, value]);

  useEffect(() => {
    if (!autoFocus) return;
    const t = setTimeout(() => {
      inputsRef.current[0]?.focus?.();
    }, 0);
    return () => clearTimeout(t);
  }, [autoFocus]);

  const emit = (nextDigits) => {
    const nextValue = nextDigits.join('');
    onChange?.(nextValue);
    if (nextDigits.every((d) => d !== '') && nextDigits.length === length) {
      onComplete?.(nextValue);
    }
  };

  const setAt = (index, char) => {
    const next = [...digits];
    next[index] = char;
    if (typeof value !== 'string') {
      setInternal(next);
    }
    emit(next);
  };

  const handleChange = (index, raw) => {
    if (disabled) return;
    const cleaned = (raw || '').replace(/\D/g, '');

    if (!cleaned) {
      setAt(index, '');
      return;
    }

    if (cleaned.length > 1) {
      const next = [...digits];
      const chars = cleaned.slice(0, length).split('');
      for (let i = 0; i < length; i += 1) {
        next[i] = chars[i] ?? next[i] ?? '';
      }
      if (typeof value !== 'string') {
        setInternal(next);
      }
      emit(next);

      const lastFilled = Math.min(chars.length - 1, length - 1);
      inputsRef.current[lastFilled]?.focus?.();
      return;
    }

    setAt(index, cleaned);

    if (index < length - 1) {
      inputsRef.current[index + 1]?.focus?.();
    }
  };

  const handleKeyDown = (index, e) => {
    if (disabled) return;

    if (e.key === 'Backspace') {
      if (digits[index]) {
        setAt(index, '');
        return;
      }
      if (index > 0) {
        inputsRef.current[index - 1]?.focus?.();
        setAt(index - 1, '');
      }
    }

    if (e.key === 'ArrowLeft' && index > 0) {
      inputsRef.current[index - 1]?.focus?.();
    }

    if (e.key === 'ArrowRight' && index < length - 1) {
      inputsRef.current[index + 1]?.focus?.();
    }
  };

  const handlePaste = (index, e) => {
    if (disabled) return;
    
    e.preventDefault();
    const pasteData = e.clipboardData.getData('text');
    const cleaned = pasteData.replace(/\D/g, '').slice(0, length);
    
    if (cleaned.length > 0) {
      const next = [...digits];
      const chars = cleaned.split('');
      
      // Fill from current index onwards
      for (let i = 0; i < length - index && i < chars.length; i++) {
        next[index + i] = chars[i];
      }
      
      if (typeof value !== 'string') {
        setInternal(next);
      }
      emit(next);
      
      // Focus the last filled field or next empty field
      const lastFilled = Math.min(index + chars.length - 1, length - 1);
      inputsRef.current[lastFilled]?.focus?.();
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          gap: 1.25,
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%',
          mb: helperText || error ? 1 : 0,
        }}
      >
        {digits.map((d, idx) => (
          <Box
            key={idx}
            component="input"
            ref={(el) => {
              if (el) inputsRef.current[idx] = el;
            }}
            value={d}
            onChange={(e) => handleChange(idx, e.target.value)}
            onKeyDown={(e) => handleKeyDown(idx, e)}
            onPaste={(e) => handlePaste(idx, e)}
            inputMode="numeric"
            pattern="[0-9]*"
            autoComplete="one-time-code"
            disabled={disabled}
            maxLength={1}
            sx={(theme) => ({
              width: 44,
              height: 52,
              borderRadius: 2,
              border: '1.5px solid',
              borderColor: error ? theme.palette.error.main : theme.palette.primary.main,
              textAlign: 'center',
              fontSize: 22,
              fontWeight: 700,
              outline: 'none',
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : '#F9F9F9',
              color: theme.palette.text.primary,
              '&:disabled': {
                opacity: 0.7,
                cursor: 'not-allowed',
              },
              '&:focus': {
                boxShadow: error
                  ? `0 0 0 3px ${theme.palette.error.main}33`
                  : `0 0 0 3px ${theme.palette.primary.main}33`,
              },
            })}
          />
        ))}
      </Box>

      {(helperText || error) && (
        <Typography
          variant="body2"
          sx={(theme) => ({
            textAlign: 'center',
            color: error ? theme.palette.error.main : theme.palette.text.secondary,
          })}
        >
          {error || helperText}
        </Typography>
      )}
    </Box>
  );
};

export default OTPEditor;
